import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { ThemeToggle } from '../components/ThemeToggle';

const DEMO_USERS: { label: string; email: string; role: 'ADMIN' | 'QA_MANAGER' | 'AUDITOR' | 'READ_ONLY' }[] =
  [
    { label: 'Yönetici (ADMIN)', email: 'admin@se-havacilik.com.tr', role: 'ADMIN' },
    { label: 'Kalite Yönetim Temsilcisi (KYT)', email: 'kyt@se-havacilik.com.tr', role: 'QA_MANAGER' },
    { label: 'İç Tetkikçi', email: 'tetkik@se-havacilik.com.tr', role: 'AUDITOR' },
    { label: 'Görüntüleyici', email: 'read@se-havacilik.com.tr', role: 'READ_ONLY' },
  ];

interface AuthInfo {
  allowDevLogin: boolean;
}

export function LoginPage() {
  const { login, devLogin, loading } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState<AuthInfo | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showDev, setShowDev] = useState(false);
  const [devEmail, setDevEmail] = useState(DEMO_USERS[1]!.email);
  const [devRole, setDevRole] = useState<typeof DEMO_USERS[number]['role']>(DEMO_USERS[1]!.role);

  useEffect(() => {
    api.get<AuthInfo>('/v1/auth/info').then(setInfo).catch(() => setInfo({ allowDevLogin: false }));
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Giriş başarısız');
    }
  }

  async function onDevSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await devLogin(devEmail, devRole);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Dev giriş başarısız');
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-gradient-to-br from-brand-50 via-white to-slate-100 px-4 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      <div className="absolute right-4 top-4">
        <ThemeToggle />
      </div>
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="text-xs font-semibold uppercase tracking-widest text-brand-700 dark:text-brand-300">
            SE Havacılık ve Makina San. Tic. A.Ş.
          </div>
          <h1 className="mt-2 text-2xl font-semibold text-slate-900 dark:text-slate-100">
            AS9100D Kalite Sistemi
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            KYS Rev.00 — 05.05.2025 · §8.3 (Tasarım) hariç
          </p>
        </div>

        <form onSubmit={onSubmit} className="card space-y-4 p-6">
          <div>
            <label className="label" htmlFor="email">
              E-posta
            </label>
            <input
              id="email"
              type="email"
              required
              autoComplete="username"
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
            />
          </div>

          <div>
            <label className="label" htmlFor="password">
              Şifre
            </label>
            <input
              id="password"
              type="password"
              required
              autoComplete="current-password"
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          {error && (
            <div className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700 ring-1 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:ring-rose-900">
              {error}
            </div>
          )}

          <button type="submit" disabled={loading} className="btn-primary w-full">
            {loading ? 'Giriş yapılıyor…' : 'Giriş Yap'}
          </button>

          {info?.allowDevLogin && (
            <div className="border-t border-slate-200 pt-3 dark:border-slate-800">
              <button
                type="button"
                onClick={() => setShowDev((v) => !v)}
                className="text-xs text-slate-500 underline-offset-2 hover:text-slate-700 hover:underline dark:text-slate-400 dark:hover:text-slate-200"
              >
                {showDev ? 'Geliştirici girişini gizle' : 'Geliştirici girişi (şifresiz)'}
              </button>
            </div>
          )}
        </form>

        {info?.allowDevLogin && showDev && (
          <form
            onSubmit={onDevSubmit}
            className="card mt-4 space-y-3 border border-amber-300 p-5 ring-amber-200 dark:border-amber-700"
          >
            <div className="text-xs font-medium uppercase tracking-wide text-amber-700 dark:text-amber-300">
              ⚠ Geliştirme Modu — sadece üretim dışı
            </div>
            <div>
              <label className="label" htmlFor="dev-email">
                E-posta
              </label>
              <input
                id="dev-email"
                type="email"
                required
                className="input"
                value={devEmail}
                onChange={(e) => setDevEmail(e.target.value)}
              />
            </div>
            <div>
              <label className="label" htmlFor="dev-role">
                Rol
              </label>
              <select
                id="dev-role"
                className="input"
                value={devRole}
                onChange={(e) => {
                  const next = DEMO_USERS.find((u) => u.role === e.target.value);
                  if (next) {
                    setDevRole(next.role);
                    setDevEmail(next.email);
                  }
                }}
              >
                {DEMO_USERS.map((u) => (
                  <option key={u.role} value={u.role}>
                    {u.label}
                  </option>
                ))}
              </select>
            </div>
            <button type="submit" disabled={loading} className="btn-secondary w-full">
              {loading ? 'Giriş…' : 'Dev Giriş'}
            </button>
          </form>
        )}

        <p className="mt-6 text-center text-xs text-slate-400 dark:text-slate-500">
          Tüm LLM &amp; veri işlemleri on-prem — savunma sanayi uyumlu.
        </p>
      </div>
    </div>
  );
}
